import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
  makeStyles,
  shorthands,
  Text,
  Button,
  Spinner,
  Badge,
  tokens,
  Input,
  Dropdown,
  Option,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableColumnDefinition,
  createTableColumn,
  Card,
  Body1,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover } from
'@fluentui/react-components';
import {
  ArrowClockwise24Regular,
  ArrowDownload24Regular,
  Filter24Regular,
  ChevronRightRegular,
  ChevronDownRegular,
  InfoRegular,
  WarningRegular,
  ErrorCircleRegular,
  CheckmarkCircleRegular,
  DocumentTableRegular,
  DocumentTextRegular } from
'@fluentui/react-icons';
import { auditService } from '../services/auditService';
import { AuditEvent } from '../types/audit';
import { useMSALAuth } from '../contexts/MSALAuthContext';
import { useError } from '../contexts/ErrorContext';
import useEventTracker from '../hooks/useEventTracker';
import { ElementButton, PageName } from '../tracking/tracking.types';

const useStyles = makeStyles({
  logs: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
    width: '100%',
    maxWidth: '100%',
    ...shorthands.padding('0', '24px', '24px', '0'),
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.borderBottom('3px', 'solid', '#0047AB'),
    ...shorthands.padding('0', '0', '20px', '0'),
    ...shorthands.margin('0', '0', '24px', '0')
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a1a1a',
    letterSpacing: '-0.5px'
  },
  controls: {
    display: 'flex',
    ...shorthands.gap('12px')
  },
  filters: {
    display: 'flex',
    flexWrap: 'nowrap',
    ...shorthands.gap('8px'),
    alignItems: 'center',
    ...shorthands.padding('8px', '12px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('6px'),
    ...shorthands.margin('0', '0', '12px', '0'),
    overflowX: 'auto'
  },
  filterField: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    minWidth: 'fit-content'
  },
  compactLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontWeight: '500',
    minWidth: 'fit-content',
    whiteSpace: 'nowrap'
  },
  emptyState: {
    textAlign: 'center',
    ...shorthands.padding('64px', '32px')
  },
  emptyStateTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    ...shorthands.margin('0', '0', '16px', '0')
  },
  emptyStateDescription: {
    fontSize: '16px',
    color: '#605e5c',
    lineHeight: '1.5',
    maxWidth: '500px',
    margin: '0 auto'
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('48px')
  },
  tableContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('6px'),
    ...shorthands.padding('16px'),
    overflowX: 'auto',
    overflowY: 'visible',
    width: '100%',
    boxSizing: 'border-box',
    boxShadow: tokens.shadow2
  },
  expandButton: {
    minWidth: '24px',
    minHeight: '24px',
    ...shorthands.padding('2px')
  },
  expandedRow: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding('12px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2)
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '150px 1fr',
    ...shorthands.gap('12px', '16px'),
    marginTop: '8px'
  },
  detailLabel: {
    fontWeight: '600',
    color: tokens.colorNeutralForeground3
  },
  detailValue: {
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word'
  },
  levelIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('4px')
  },
  codeBlock: {
    fontFamily: 'Consolas, Monaco, monospace',
    fontSize: '12px',
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    ...shorthands.padding('8px'),
    ...shorthands.borderRadius('4px'),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowX: 'auto'
  },
  syntaxHighlight: {
    '& .keyword': { color: '#569cd6', fontWeight: '600' },
    '& .string': { color: '#ce9178' },
    '& .number': { color: '#b5cea8' },
    '& .comment': { color: '#6a9955', fontStyle: 'italic' },
    '& .function': { color: '#dcdcaa', fontWeight: '500' },
    '& .property': { color: '#9cdcfe' },
    '& .error': { color: '#f48771', fontWeight: '600' },
    '& .warning': { color: '#ffd700', fontWeight: '500' },
    '& .success': { color: '#4ec9b0', fontWeight: '500' },
    '& .cmdlet': { color: '#c586c0', fontWeight: '600' },
    '& .parameter': { color: '#9cdcfe' },
    '& .operator': { color: '#d4d4d4' },
    '& .variable': { color: '#4fc1ff' }
  },
  logLine: {
    ...shorthands.padding('2px', '0'),
    lineHeight: '1.4',
    '&.error': {
      color: '#f48771',
      fontWeight: '500'
    },
    '&.warning': {
      color: '#cca700'
    },
    '&.success': {
      color: '#4ec9b0'
    },
    '&.info': {
      color: '#9cdcfe'
    }
  }
});

interface LogRowData extends AuditEvent {
  expanded?: boolean;
}

export const Logs: React.FC = () => {
  const styles = useStyles();
  const { user } = useMSALAuth();
  const { showError } = useError();
  const eventTracker = useEventTracker();

  const [logs, setLogs] = useState<LogRowData[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogRowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const formatTimestamp = (timestamp: string | Date) => {
    try {
      if (timestamp instanceof Date) {
        return timestamp.toLocaleString();
      }
      return new Date(timestamp).toLocaleString();
    } catch {
      return String(timestamp);
    }
  };

  const toggleRowExpansion = (logId: string) => {
    const isCurrentlyExpanded = expandedRows.has(logId);

    eventTracker.eventTrackOnElementClicked({
      pageName: PageName.LOGS,
      elementName: isCurrentlyExpanded ? ElementButton.COLLAPSE_ROW : ElementButton.EXPAND_ROW,
      elementType: 'button'
    });

    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const getLevelIcon = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return <ErrorCircleRegular style={{ color: tokens.colorPaletteRedForeground1 }} data-testid="logs-error-circle-regular" />;
      case 'warning':
        return <WarningRegular style={{ color: tokens.colorPaletteYellowForeground2 }} data-testid="logs-warning-regular" />;
      case 'success':
        return <CheckmarkCircleRegular style={{ color: tokens.colorPaletteGreenForeground1 }} data-testid="logs-checkmark-circle-regular" />;
      default:
        return <InfoRegular style={{ color: tokens.colorNeutralForeground2 }} data-testid="logs-info-regular" />;
    }
  };

  const getLevelBadgeColor = (level: string): "brand" | "danger" | "important" | "informative" | "severe" | "subtle" | "success" | "warning" => {
    switch (level?.toLowerCase()) {
      case 'error':
        return 'danger';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      default:
        return 'informative';
    }
  };

  const columns: TableColumnDefinition<LogRowData>[] = [
  createTableColumn<LogRowData>({
    columnId: 'expand',
    renderHeaderCell: () => '',
    renderCell: (item) =>
    <Button
      className={styles.expandButton}
      appearance="subtle"
      size="small"
      icon={expandedRows.has(item.id) ? <ChevronDownRegular data-testid="logs-chevron-down-regular" /> : <ChevronRightRegular data-testid="logs-chevron-right-regular" />}
      onClick={() => toggleRowExpansion(item.id)}
      aria-label={expandedRows.has(item.id) ? "Collapse row" : "Expand row"} data-testid="logs-button" />,


    compare: () => 0
  }),
  createTableColumn<LogRowData>({
    columnId: 'timestamp',
    renderHeaderCell: () => 'Timestamp',
    renderCell: (item) =>
    <Text size={200} data-testid="logs-timestamp-text">{formatTimestamp(item.timestamp)}</Text>,

    compare: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  }),
  createTableColumn<LogRowData>({
    columnId: 'category',
    renderHeaderCell: () => 'Category',
    renderCell: (item) => {
      // Extract category from resource field or action prefix
      let category = item.resource || 'System';

      // Try to extract category from action if not in resource
      if ((!category || category === 'System') && item.action) {
        const actionPrefixes = {
          'POLICY': 'Policy',
          'DOMAIN': 'Domain',
          'AUTH': 'Auth',
          'TEAMS': 'Teams',
          'DRIFT': 'Drift',
          'SYNC': 'Sync',
          'CONFIG': 'Configuration',
          'POWERSHELL': 'PowerShell',
          'LSEG': 'LSEG',
          'EMAIL': 'Email',
          'WEBHOOK': 'Integration',
          'API': 'API',
          'USER': 'User',
          'GROUP': 'Security Group',
          'AUDIT': 'Audit'
        };

        for (const [prefix, label] of Object.entries(actionPrefixes)) {
          if (item.action.toUpperCase().startsWith(prefix)) {
            category = label;
            break;
          }
        }
      }

      if (item.details && typeof item.details === 'object' && item.details.category) {
        category = item.details.category;
      }

      // Enhanced color mapping for better visual distinction
      const categoryColors: Record<string, any> = {
        'Policy': 'brand',
        'Drift': 'danger',
        'Auth': 'success',
        'Domain': 'informative',
        'Teams': 'important',
        'Integration': 'subtle',
        'System': 'neutral',
        'Audit': 'severe',
        'PowerShell': 'warning',
        'LSEG': 'teal',
        'Configuration': 'subtle',
        'Sync': 'brand',
        'Email': 'informative',
        'API': 'important',
        'User': 'success',
        'Security Group': 'severe',
        'Webhook': 'subtle'
      };

      const color = categoryColors[category] || 'neutral';

      return (
        <Badge appearance="filled" color={color} size="small" data-testid="logs-category-badge">
            {category}
          </Badge>);

    },
    compare: (a, b) => (a.resource || '').localeCompare(b.resource || '')
  }),
  createTableColumn<LogRowData>({
    columnId: 'action',
    renderHeaderCell: () => 'Action',
    renderCell: (item) =>
    <Text weight="semibold" size={300} data-testid="logs-action-text">
          {item.action ? item.action.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : 'N/A'}
        </Text>,

    compare: (a, b) => (a.action || '').localeCompare(b.action || '')
  }),
  createTableColumn<LogRowData>({
    columnId: 'user',
    renderHeaderCell: () => 'User',
    renderCell: (item) =>
    <Text size={200} data-testid="logs-user-email-text">{item.userEmail || 'System'}</Text>,

    compare: (a, b) => (a.userEmail || '').localeCompare(b.userEmail || '')
  }),
  createTableColumn<LogRowData>({
    columnId: 'result',
    renderHeaderCell: () => 'Result',
    renderCell: (item) => item.result ?
    <Badge
      appearance="filled"
      color={item.result === 'Success' ? 'success' : item.result === 'Failure' ? 'danger' : 'warning'}
      size="small" data-testid="logs-result-badge">

          {item.result}
        </Badge> :
    <Text size={200} data-testid="logs-result-empty-text">-</Text>,
    compare: (a, b) => (a.result || '').localeCompare(b.result || '')
  })];


  const loadLogs = useCallback(async (isRetry = false) => {
    try {
      if (!isRetry) {
        setLoading(true);
        setError(null);
      }

      const response = await auditService.searchAuditLogs({
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // Last 7 days
        endDate: new Date(),
        pageSize: 500
      });

      setError(null);
      setLogs(response?.events || []);
    } catch (err: any) {
      console.error('Failed to load audit logs:', err);
      const errorMessage = err.message || 'Failed to load audit logs';
      setError(errorMessage);
      setLogs([]);

      const isTemporaryError = errorMessage.includes('temporarily unavailable') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('500');

      showError({
        message: errorMessage,
        title: 'Failed to Load Audit Logs',
        severity: isTemporaryError ? 'warning' : 'error',
        details: err.stack || err.toString(),
        onRetry: isTemporaryError ? () => loadLogs(false) : undefined
      });

      if (isTemporaryError) {
        setTimeout(() => {
          console.log('Auto-retrying audit log load...');
          loadLogs(true);
        }, 10000);
      }
    } finally {
      if (!isRetry) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    let filtered = logs;

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter((log) =>
      log?.summary?.toLowerCase().includes(search) ||
      typeof log?.details === 'string' && log?.details.toLowerCase().includes(search) ||
      log?.action?.toLowerCase().includes(search) ||
      log?.userEmail?.toLowerCase().includes(search)
      );
    }

    if (selectedLevel) {
      filtered = filtered.filter((log) => log.level === selectedLevel);
    }

    if (selectedCategory) {
      filtered = filtered.filter((log) => log.category === selectedCategory);
    }

    if (selectedUser) {
      filtered = filtered.filter((log) => log.userEmail === selectedUser);
    }

    setFilteredLogs(filtered);
  }, [logs, searchText, selectedLevel, selectedCategory, selectedUser]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  useEffect(() => {
    if (searchText && searchText.length >= 2) {
      const timeoutId = setTimeout(() => {
        eventTracker.eventTrackOnElementClicked({
          pageName: PageName.LOGS,
          elementName: ElementButton.SEARCH,
          elementType: 'input'
        });
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [searchText]);

  const handleApplyFilter = useCallback(() => {
    eventTracker.eventTrackOnElementClicked({
      pageName: PageName.LOGS,
      elementName: ElementButton.APPLY_FILTER,
      elementType: 'button'
    });
    loadLogs();
  }, [loadLogs]);

  const handleRefresh = useCallback(() => {
    eventTracker.eventTrackOnElementClicked({
      pageName: PageName.LOGS,
      elementName: ElementButton.REFRESH,
      elementType: 'button'
    });
    loadLogs();
  }, [loadLogs]);

  // Export handler: download logs in specified format
  const handleExport = useCallback(async (format: 'csv' | 'json') => {
    eventTracker.eventTrackOnElementClicked({
      pageName: PageName.LOGS,
      elementName: format === 'csv' ? ElementButton.EXPORT_CSV : ElementButton.EXPORT_JSON,
      elementType: 'menu-item'
    });

    try {
      // Build criteria from current filters
      const criteria = {
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        endDate: new Date(),
        userId: selectedUser || undefined,
        level: selectedLevel || undefined,
        category: selectedCategory || undefined,
        searchText: searchText || undefined,
        pageSize: 10000 // try to export a large set
      } as any;

      const blob = await auditService.exportAuditLogs(format, criteria);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `audit-logs-${timestamp}.${format}`;

      // Create a download link and click it
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      // Log a client-side audit event for the export action
      try {
        auditService.logClientEvent(`Export: ${format.toUpperCase()}`);
      } catch (e) {
        // swallow audit logging errors
        console.warn('Failed to log export event', e);
      }
    } catch (err: any) {
      console.error('Export failed:', err);
      showError({
        title: 'Export Failed',
        message: err.message || 'Failed to export audit logs',
        severity: 'error'
      });
    }
  }, [selectedUser, selectedLevel, selectedCategory, searchText, showError]);

  const uniqueLevels = Array.from(new Set(logs.map((log) => log.level).filter(Boolean)));
  const uniqueCategories = Array.from(new Set(logs.map((log) => log.category).filter(Boolean)));
  const uniqueUsers = Array.from(new Set(logs.map((log) => log.userEmail).filter(Boolean)));

  const formatLogContent = (content: string) => {
    // Color-code PowerShell commands and log entries
    const lines = content.split('\n');
    return lines.map((line, index) => {
      let className = '';
      let formattedLine = line;

      // Detect log level patterns
      if (line.includes('[ERROR]') || line.includes('Error:') || line.includes('Failed')) {
        className = 'error';
      } else if (line.includes('[WARNING]') || line.includes('Warning:')) {
        className = 'warning';
      } else if (line.includes('[SUCCESS]') || line.includes('Success:') || line.includes('✅')) {
        className = 'success';
      } else if (line.includes('[INFO]') || line.includes('Info:')) {
        className = 'info';
      }

      // Enhanced PowerShell syntax highlighting
      formattedLine = formattedLine
      // PowerShell cmdlets
      .replace(/(Get-\w+|Set-\w+|New-\w+|Remove-\w+|Update-\w+|Connect-\w+|Invoke-\w+|Start-\w+|Stop-\w+|Test-\w+)/g, '<span class="cmdlet">$1</span>')
      // Parameters (starting with -)
      .replace(/(-\w+)(?=\s|:|$)/g, '<span class="parameter">$1</span>')
      // Strings
      .replace(/(['"])([^'"]*)['"](?![\w])/g, '<span class="string">$1$2$1</span>')
      // Boolean and null values
      .replace(/\b(true|false|null|undefined|\$true|\$false|\$null)\b/gi, '<span class="keyword">$1</span>')
      // Variables
      .replace(/(\$\w+)/g, '<span class="variable">$1</span>')
      // Numbers
      .replace(/\b(\d+)\b/g, '<span class="number">$1</span>')
      // Operators
      .replace(/([=\+\-\*\/\|&<>!]+)/g, '<span class="operator">$1</span>');

      // Sanitize the HTML to prevent XSS attacks
      const sanitizedLine = DOMPurify.sanitize(formattedLine, {
        ALLOWED_TAGS: ['span'],
        ALLOWED_ATTR: ['class']
      });

      return (
        <div key={index} className={`${styles.logLine} ${className}`}
        dangerouslySetInnerHTML={{ __html: sanitizedLine }} data-testid="logs-formatted-line-div" />);

    });
  };

  const renderExpandedContent = (item: LogRowData) =>
  <div className={styles.expandedRow} data-testid="logs-expanded-row-div">
      <div className={styles.detailsGrid} data-testid="logs-details-grid-div">
        {item.category &&
      <>
            <Text className={styles.detailLabel} data-testid="logs-detail-category-label-text">Category:</Text>
            <Text className={styles.detailValue} data-testid="logs-detail-category-value-text">{item.category}</Text>
          </>
      }

        {item.ipAddress && item.ipAddress !== 'N/A' &&
      <>
            <Text className={styles.detailLabel} data-testid="logs-ip-address-text">IP Address:</Text>
            <Text className={styles.detailValue} data-testid="logs-detail-ip-address-value-text">{item.ipAddress}</Text>
          </>
      }

        {item.browserType && item.browserType !== 'N/A' &&
      <>
            <Text className={styles.detailLabel} data-testid="logs-browser-text">Browser:</Text>
            <Text className={styles.detailValue} data-testid="logs-detail-browser-value-text">{item.browserType}</Text>
          </>
      }

        {item.operatingSystem && item.operatingSystem !== 'N/A' &&
      <>
            <Text className={styles.detailLabel} data-testid="logs-operating-system-text">Operating System:</Text>
            <Text className={styles.detailValue} data-testid="logs-detail-operating-system-value-text">{item.operatingSystem}</Text>
          </>
      }

        {item.details &&
      <>
            <Text className={styles.detailLabel} data-testid="logs-details-text">Details:</Text>
            <div className={styles.detailValue} data-testid="logs-detail-value-div">
              {typeof item.details === 'string' ?
          <div className={`${styles.codeBlock} ${styles.syntaxHighlight}`} data-testid="logs-details-code-block-div">
                  {formatLogContent(item.details)}
                </div> :

          <pre className={`${styles.codeBlock} ${styles.syntaxHighlight}`} data-testid="logs-details-json-pre">
                  {JSON.stringify(item.details, null, 2)}
                </pre>
          }
            </div>
          </>
      }
      </div>
    </div>;


  return (
    <div className={styles.logs} data-testid="logs-logs-div">
      <div className={styles.header} data-testid="logs-header-div">
        <Text className={styles.title} data-testid="logs-audit-logs-text">Audit Logs</Text>
        <div className={styles.controls} data-testid="logs-controls-div">
          <Button
            icon={<ArrowClockwise24Regular data-testid="logs-arrow-clockwise24-regular" />}
            onClick={handleRefresh}
            disabled={loading} data-testid="logs-refresh-button">

            Refresh
          </Button>
          <Menu data-testid="logs-menu">
            <MenuTrigger disableButtonEnhancement data-testid="logs-menu-trigger">
              <Button
                icon={<ArrowDownload24Regular data-testid="logs-arrow-download24-regular" />}
                appearance="primary" data-testid="logs-export-button">

                Export
              </Button>
            </MenuTrigger>
            <MenuPopover data-testid="logs-menu-popover">
              <MenuList data-testid="logs-menu-list">
                <MenuItem
                  icon={<DocumentTableRegular data-testid="logs-document-table-regular" />}
                  onClick={() => handleExport('csv')} data-testid="logs-export-as-csv-menu-item">

                  Export as CSV
                </MenuItem>
                <MenuItem
                  icon={<DocumentTextRegular data-testid="logs-document-text-regular" />}
                  onClick={() => handleExport('json')} data-testid="logs-export-as-json-menu-item">

                  Export as JSON
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </div>

      <div className={styles.filters} data-testid="logs-filters-div">
        <div className={styles.filterField} data-testid="logs-filter-field-div">
          <Text className={styles.compactLabel} data-testid="logs-search-text">Search:</Text>
          <Input
            placeholder="Search logs..."
            size="small"
            value={searchText}
            onChange={(e, data) => handleSearchChange(data.value)}
            style={{ width: '180px' }} data-testid="logs-search-logs-input" />

        </div>

        <div className={styles.filterField} data-testid="logs-filter-field-level">
          <Text className={styles.compactLabel} data-testid="logs-level-text">Level:</Text>
          <Dropdown
            placeholder="All"
            size="small"
            value={selectedLevel}
            onOptionSelect={(e, data) => setSelectedLevel(data.optionValue || '')}
            style={{ minWidth: '100px' }} data-testid="logs-level-dropdown">

            <Option value="" text="All" data-testid="logs-level-all-option">All</Option>
            {uniqueLevels.map((level, index) =>
            <Option key={level} value={level} text={level || ''} data-testid={`logs-option-${index}`}>{level}</Option>
            )}
          </Dropdown>
        </div>

        <div className={styles.filterField} data-testid="logs-filter-field-category">
          <Text className={styles.compactLabel} data-testid="logs-filter-category-label-text">Category:</Text>
          <Dropdown
            placeholder="All"
            size="small"
            value={selectedCategory}
            onOptionSelect={(e, data) => setSelectedCategory(data.optionValue || '')}
            style={{ minWidth: '120px' }} data-testid="logs-category-dropdown">

            <Option value="" text="All" data-testid="logs-category-all-option">All</Option>
            {uniqueCategories.map((category, index) =>
            <Option key={category} value={category} text={category || ''} data-testid={`logs-option-${index}`}>{category}</Option>
            )}
          </Dropdown>
        </div>

        <div className={styles.filterField} data-testid="logs-filter-field-user">
          <Text className={styles.compactLabel} data-testid="logs-user-text">User:</Text>
          <Dropdown
            placeholder="All"
            size="small"
            value={selectedUser}
            onOptionSelect={(e, data) => setSelectedUser(data.optionValue || '')}
            style={{ minWidth: '150px' }} data-testid="logs-user-dropdown">

            <Option value="" text="All" data-testid="logs-user-all-option">All</Option>
            {uniqueUsers.map((user, index) =>
            <Option key={user} value={user} text={user || ''} data-testid={`logs-option-${index}`}>{user}</Option>
            )}
          </Dropdown>
        </div>

        <Button
          icon={<Filter24Regular data-testid="logs-filter24-regular" />}
          onClick={handleApplyFilter}
          appearance="primary"
          size="small" data-testid="logs-apply-button">

          Apply
        </Button>
      </div>

      {loading &&
      <div className={styles.loading} data-testid="logs-loading-div">
          <Spinner label="Loading audit logs..." data-testid="logs-loading-audit-logs-spinner" />
        </div>
      }

      {!loading && error &&
      <Card className={styles.emptyState} data-testid="logs-error-card">
          <Text className={styles.emptyStateTitle} data-testid="logs-unable-to-load-audit-logs-text">Unable to Load Audit Logs</Text>
          <Body1 className={styles.emptyStateDescription} data-testid="logs-body1">
            {error}
            <br data-testid="logs-error-line-break-br" />
            <Button
            appearance="primary"
            onClick={() => loadLogs(false)}
            style={{ marginTop: '16px' }} data-testid="logs-try-again-button">

              Try Again
            </Button>
          </Body1>
        </Card>
      }

      {!loading && !error && filteredLogs.length === 0 &&
      <Card className={styles.emptyState} data-testid="logs-empty-card">
          <Text className={styles.emptyStateTitle} data-testid="logs-no-audit-logs-found-text">No audit logs found</Text>
          <Body1 className={styles.emptyStateDescription} data-testid="logs-no-audit-events-found-for-the-body1">
            No audit events found for the selected filters. Try adjusting your search criteria or check back later.
          </Body1>
        </Card>
      }

      {!loading && !error && filteredLogs.length > 0 &&
      <>
          <Text size={300} style={{ color: tokens.colorNeutralForeground3, marginBottom: '8px' }} data-testid="logs-showing-text">
            Showing {filteredLogs.length} audit event{filteredLogs.length !== 1 ? 's' : ''}
          </Text>

          <div className={styles.tableContainer} data-testid="logs-table-container-div">
            <DataGrid
            items={filteredLogs}
            columns={columns}
            sortable
            resizableColumns
            getRowId={(item) => item.id}
            size="small" data-testid="logs-data-grid">

              <DataGridHeader data-testid="logs-data-grid-header">
                <DataGridRow data-testid="logs-data-grid-row">
                  {({ renderHeaderCell }) =>
                <DataGridHeaderCell data-testid="logs-data-grid-header-cell">{renderHeaderCell()}</DataGridHeaderCell>
                }
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<LogRowData> data-testid="logs-data-grid-body">
                {({ item, rowId }) =>
              <>
                    <DataGridRow<LogRowData> key={rowId} data-testid={`logs-data-grid-row-${rowId}`}>
                      {({ renderCell }) =>
                  <DataGridCell data-testid="logs-data-grid-cell">{renderCell(item)}</DataGridCell>
                  }
                    </DataGridRow>
                    {expandedRows.has(item.id) &&
                <tr data-testid="logs-expanded-details-row-tr">
                        <td colSpan={columns.length} style={{ padding: 0 }} data-testid="logs-expanded-details-cell-td">
                          {renderExpandedContent(item)}
                        </td>
                      </tr>
                }
                  </>
              }
              </DataGridBody>
            </DataGrid>
          </div>
        </>
      }
    </div>);

};